import { useState, useEffect } from "react"
import { Bell, AlertTriangle, Loader2, Info, ArrowLeft } from "lucide-react"
import { studentService, type StudentNotification } from "@/services/studentService"
import { safeLog } from '@/utils/safeLog';

interface NotificationItem {
  id: string
  sender: string
  title: string
  message: string
  urgency: 'low' | 'medium' | 'high'
  timestamp: string
  fullDate: string
  status: 'read' | 'unread'
}

const urgencyConfig = {
  high:   { dotColor: 'bg-rose-500',  tag: 'Urgent', tagClass: 'bg-rose-50 text-rose-600 border-rose-200'   },
  medium: { dotColor: 'bg-amber-500', tag: 'Alert',  tagClass: 'bg-amber-50 text-amber-600 border-amber-200' },
  low:    { dotColor: 'bg-blue-400',  tag: '',        tagClass: ''                                             },
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins  = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days  = Math.floor(diff / 86400000)
  if (mins  < 60) return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days  <  7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function NotificationPage() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [loading, setLoading]             = useState(true)
  const [unreadCount, setUnreadCount]     = useState(0)
  const [openNote, setOpenNote]           = useState<NotificationItem | null>(null)
  const [pushDismissed, setPushDismissed] = useState(false)
  const [dm, setDm]                       = useState(() => localStorage.getItem('studentDarkMode') === 'true')

  useEffect(() => {
    const handler = (e: Event) => setDm((e as CustomEvent<{ darkMode: boolean }>).detail.darkMode)
    window.addEventListener('studentDarkModeChanged', handler)
    return () => window.removeEventListener('studentDarkModeChanged', handler)
  }, [])

  useEffect(() => { fetchNotifications() }, [])

  const fetchNotifications = async () => {
    try {
      setLoading(true)
      const data = await studentService.getNotifications()
      const mapped: NotificationItem[] = data.notifications.map((n: StudentNotification) => ({
        id:        n.id,
        sender:    n.senderName,
        title:     n.title,
        message:   n.message,
        urgency:   n.type === 'critical' || n.type === 'urgent' ? 'high'
                 : n.type === 'warning' ? 'medium' : 'low',
        timestamp: relativeTime(n.createdAt),
        fullDate:  new Date(n.createdAt).toLocaleString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        }),
        status: (n.status === 'read' ? 'read' : 'unread') as 'read' | 'unread',
      }))
      setNotifications(mapped)
      setUnreadCount(data.unreadCount)
    } catch (err) {
      safeLog.error('Failed to load notifications:', err)
    } finally {
      setLoading(false)
    }
  }

  const openNotification = async (note: NotificationItem) => {
    setOpenNote(note)
    if (note.status === 'unread') {
      try {
        await studentService.markNotificationRead(note.id)
        setNotifications(prev => prev.map(n => n.id === note.id ? { ...n, status: 'read' } : n))
        setUnreadCount(prev => Math.max(0, prev - 1))
        setOpenNote({ ...note, status: 'read' })
      } catch { /* non-fatal */ }
    }
  }

  const markAllRead = async () => {
    const unread = notifications.filter(n => n.status === 'unread')
    await Promise.allSettled(unread.map(n => studentService.markNotificationRead(n.id)))
    setNotifications(prev => prev.map(n => ({ ...n, status: 'read' as const })))
    setUnreadCount(0)
  }

  const requestPush = async () => {
    if ('Notification' in window) await Notification.requestPermission()
    setPushDismissed(true)
  }

  const showPushPrompt =
    !pushDismissed &&
    unreadCount > 0 &&
    'Notification' in window &&
    Notification.permission === 'default'

  // ── Detail view ──────────────────────────────────────────────────────────
  if (openNote) {
    const cfg = urgencyConfig[openNote.urgency]
    const UrgIcon = openNote.urgency === 'low' ? Info : AlertTriangle
    return (
      <div className="px-1 py-4 space-y-5">
        <button
          onClick={() => setOpenNote(null)}
          className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${dm ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <ArrowLeft className="w-4 h-4" />
          All Notifications
        </button>

        <div className={`h-0.5 w-full rounded-full ${
          openNote.urgency === 'high' ? 'bg-rose-400' :
          openNote.urgency === 'medium' ? 'bg-amber-400' : 'bg-indigo-300'
        }`} />

        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            openNote.urgency === 'high'   ? dm ? 'bg-rose-900/50'   : 'bg-rose-100'  :
            openNote.urgency === 'medium' ? dm ? 'bg-amber-900/50'  : 'bg-amber-100' :
                                            dm ? 'bg-blue-900/50'   : 'bg-blue-100'
          }`}>
            <UrgIcon className={`w-5 h-5 ${
              openNote.urgency === 'high'   ? 'text-rose-500'  :
              openNote.urgency === 'medium' ? 'text-amber-500' : 'text-blue-500'
            }`} />
          </div>
          <div className="flex-1">
            <h1 className={`text-base font-bold leading-snug ${dm ? 'text-white' : 'text-gray-900'}`}>{openNote.title}</h1>
            {cfg.tag && (
              <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.tagClass}`}>
                {cfg.tag}
              </span>
            )}
          </div>
        </div>

        <div className={`space-y-1.5 pb-4 border-b ${dm ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex gap-4">
            <span className={`w-10 flex-shrink-0 text-xs font-medium ${dm ? 'text-white/40' : 'text-gray-400'}`}>From</span>
            <span className={`font-medium text-sm ${dm ? 'text-white' : 'text-gray-800'}`}>{openNote.sender}</span>
          </div>
          <div className="flex gap-4">
            <span className={`w-10 flex-shrink-0 text-xs font-medium ${dm ? 'text-white/40' : 'text-gray-400'}`}>Date</span>
            <span className={`text-xs ${dm ? 'text-white/60' : 'text-gray-500'}`}>{openNote.fullDate}</span>
          </div>
        </div>

        <p className={`text-sm leading-relaxed ${dm ? 'text-white/80' : 'text-gray-700'}`}>{openNote.message}</p>
      </div>
    )
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div className="px-1 py-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 backdrop-blur-sm rounded-xl flex items-center justify-center ${dm ? 'bg-white/10 border border-white/10' : 'bg-white/60 border border-white/60'}`}>
            <Bell className="w-5 h-5 text-indigo-500" />
          </div>
          <div>
            <h1 className={`font-bold leading-tight ${dm ? 'text-white' : 'text-gray-900'}`}>Notifications</h1>
            {unreadCount > 0 && <p className="text-xs text-indigo-400">{unreadCount} unread</p>}
          </div>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className={`text-xs font-medium transition-colors ${dm ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-500 hover:text-indigo-700'}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Push opt-in */}
      {showPushPrompt && (
        <div className={`backdrop-blur-sm rounded-2xl p-4 flex items-start gap-3 ${dm ? 'bg-indigo-900/40 border border-indigo-500/30' : 'bg-indigo-50/80 border border-indigo-200/70'}`}>
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${dm ? 'bg-indigo-900/60' : 'bg-indigo-100/80'}`}>
            <Bell className="w-4 h-4 text-indigo-500" />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${dm ? 'text-indigo-300' : 'text-indigo-800'}`}>Stay in the loop</p>
            <p className={`text-xs mt-0.5 ${dm ? 'text-indigo-400' : 'text-indigo-600'}`}>
              Enable notifications so you never miss an important alert.
            </p>
            <div className="flex gap-2 mt-3">
              <button onClick={requestPush} className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-xl hover:bg-indigo-700 transition-colors" style={{ border: 'none' }}>
                Enable
              </button>
              <button onClick={() => setPushDismissed(true)} className={`px-3 py-1.5 text-xs font-medium rounded-xl transition-colors ${dm ? 'text-indigo-400 hover:bg-white/10' : 'text-indigo-500 hover:bg-indigo-100'}`} style={{ border: 'none', background: 'none' }}>
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification list */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className={`w-12 h-12 rounded-full backdrop-blur-sm border flex items-center justify-center animate-pulse ${dm ? 'bg-white/10 border-white/10' : 'bg-white/60 border-white/60'}`}>
            <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
          </div>
          <span className={`text-sm ${dm ? 'text-white/60' : 'text-gray-500'}`}>Loading notifications…</span>
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className={`w-14 h-14 backdrop-blur-sm rounded-2xl flex items-center justify-center ${dm ? 'bg-white/10 border border-white/10' : 'bg-white/60 border border-white/60'}`}>
            <Bell className={`w-7 h-7 ${dm ? 'text-white/20' : 'text-gray-300'}`} />
          </div>
          <p className={`text-sm ${dm ? 'text-white/60' : 'text-gray-500'}`}>No notifications yet</p>
          <p className={`text-xs ${dm ? 'text-white/40' : 'text-gray-400'}`}>You're all caught up!</p>
        </div>
      ) : (
        <div className={`divide-y p-4 ${dm ? 'divide-white/10' : 'divide-gray-200'}`}>
          {notifications.map(note => {
            const cfg = urgencyConfig[note.urgency]
            const isUnread = note.status === 'unread'
            return (
              <div
                key={note.id}
                role="button"
                tabIndex={0}
                onClick={() => openNotification(note)}
                onKeyDown={e => e.key === 'Enter' && openNotification(note)}
                className={`flex items-start gap-3 py-3.5 px-3 cursor-pointer transition-all rounded-xl ${
                  isUnread
                    ? dm ? 'bg-indigo-900/30 hover:bg-indigo-900/50' : 'bg-indigo-50/30 hover:bg-indigo-50/60'
                    : dm ? 'hover:bg-white/5' : 'hover:bg-indigo-50/40'
                }`}
              >
                {/* Sender avatar with unread dot */}
                <div className="relative flex-shrink-0 mt-0.5">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                    note.urgency === 'high'   ? dm ? 'bg-rose-900/50 text-rose-300'   : 'bg-rose-100 text-rose-700'   :
                    note.urgency === 'medium' ? dm ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700' :
                                               dm ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
                  }`}>
                    {note.sender.charAt(0).toUpperCase()}
                  </div>
                  {isUnread && (
                    <span className={`absolute -top-0.5 -right-0.5 block w-2.5 h-2.5 rounded-full border-2 ${dm ? 'border-slate-900' : 'border-white'} ${cfg.dotColor}`} />
                  )}
                </div>

                {/* Text content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 mb-0.5">
                    <p className={`text-sm truncate ${
                      isUnread
                        ? dm ? 'font-semibold text-white' : 'font-semibold text-gray-900'
                        : dm ? 'font-medium text-white/60' : 'font-medium text-gray-600'
                    }`}>
                      {note.title}
                    </p>
                    <span className={`text-[11px] flex-shrink-0 whitespace-nowrap ${dm ? 'text-white/40' : 'text-gray-400'}`}>{note.timestamp}</span>
                  </div>
                  <p className={`text-xs truncate ${isUnread ? dm ? 'text-white/60' : 'text-gray-500' : dm ? 'text-white/40' : 'text-gray-400'}`}>
                    <span className="font-medium">{note.sender}</span> — {note.message}
                  </p>
                </div>

                {/* Urgency badge (high only) */}
                {note.urgency === 'high' && (
                  <span className={`flex-shrink-0 self-center ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${cfg.tagClass}`}>
                    Urgent
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div className="h-2" />
    </div>
  )
}
